import { useState } from 'react';
import { useParams } from 'react-router';
import { findPayday } from '@tahi/core';
import {
  confirmPayday,
  editReceivedPayday,
  skipPayday,
  undoPayday,
  type BudgetCommandResult,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Sheet, showToast } from '../../../ui';
import { useGoBack } from '../../spending/hooks';
import { PaydayConfirm, paydayTitle } from '../components/PaydayConfirm';
import { copy, errorText } from '../copy';
import { useBudgetToday, useToday } from '../hooks';

export function PaydayRoute() {
  const { sourceId = '', date = '' } = useParams();
  const env = useCommandEnv();
  const goBack = useGoBack('/m/budget/income');
  const today = useToday();
  const s = useBudgetToday();
  const [error, setError] = useState<string | null>(null);

  if (!s) return null;
  const payday = findPayday(s.userId, s.sources, s.rows, sourceId, date);
  const source = s.sources.find((x) => x.id === sourceId);
  if (!payday || !source) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.income}>
        <p className="text-muted">{copy.paydayNotFound}</p>
      </Sheet>
    );
  }

  const accounts = s.accounts;
  const settings = s.settings;
  const undo = () => void undoPayday(env, sourceId, date);
  function done(r: BudgetCommandResult, message: string, withUndo: boolean) {
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    showToast({ message, ...(withUndo ? { action: { label: copy.undo, onPress: undo } } : {}) });
    goBack();
  }

  return (
    <Sheet open onOpenChange={(open) => !open && goBack()} title={paydayTitle(payday, today)}>
      <PaydayConfirm
        payday={payday}
        today={today}
        canSkip={source.kind === 'bonus'}
        error={error}
        accounts={accounts}
        defaultAccountId={
          (source.accountId && accounts.some((a) => a.id === source.accountId)
            ? source.accountId
            : null) ??
          settings?.incomeAccountId ??
          null
        }
        onConfirm={async (amountMinor, d, accountId) =>
          done(
            await confirmPayday(env, sourceId, date, { amountMinor, date: d, accountId }),
            copy.receivedToast(amountMinor),
            true,
          )
        }
        onSkip={async () => done(await skipPayday(env, sourceId, date), copy.skippedToast, true)}
        onUndo={async () => done(await undoPayday(env, sourceId, date), copy.undoneToast, false)}
        onEditReceived={async (amountMinor, d) =>
          done(
            await editReceivedPayday(env, sourceId, date, { amountMinor, date: d }),
            copy.receivedToast(amountMinor),
            false,
          )
        }
        onClose={goBack}
      />
    </Sheet>
  );
}
